import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hashKey, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import {
  getAppHomeStreamSeq,
  readAppHomeEventsSince,
  subscribeAppHomeEvents,
  type AppHomeEventMeta,
  type AppHomeStreamEvent,
} from '../../lib/sseStore';
import { logger } from '../../lib/logger';

const STREAM_KEY = { view: 'active', filter: 'all' } as const;
const DEFAULT_COALESCE_MS = 500;
const OPEN_FRAME: AppHomeStreamEvent = { type: 'open' };

/**
 * Per QueryClient, the stream cursor each consumer had reached when it last
 * unmounted, keyed by the hash of its reconnect keys. Outlives the component,
 * like the cache it guards; a fresh QueryClient (new cache) starts empty.
 */
const unmountCursors = new WeakMap<QueryClient, Map<string, number>>();

function cursorsFor(queryClient: QueryClient): Map<string, number> {
  let cursors = unmountCursors.get(queryClient);
  if (!cursors) {
    cursors = new Map();
    unmountCursors.set(queryClient, cursors);
  }
  return cursors;
}

/**
 * What to do when the stream reopens after a real drop. The server does not
 * replay frames, so `'invalidate'` resyncs by invalidating `keys`. A function
 * decides per reconnect.
 */
export type StreamReconnectPolicy = 'invalidate' | 'ignore' | ((queryClient: QueryClient) => boolean);

export type StreamInvalidationOptions = {
  /** Which frames invalidate. Read from a ref, so an inline closure is fine. */
  when: (event: AppHomeStreamEvent) => boolean;
  /**
   * The first match in a quiet period invalidates at once and opens a window
   * of this length (default 500ms). Matches inside the window are collected
   * and flushed once, one invalidation per key, when it ends. The window
   * re-arms only if that flush invalidated something. It does not reset on
   * new matches, so a steady stream still flushes once per window.
   */
  coalesceMs?: number;
  /**
   * Which frames sent while this hook was not subscribed invalidate on mount
   * (default: `when`). Use it when a frame this hook ignores live is still
   * handled by a sibling subscriber that is also gone, e.g. a field patch.
   */
  whenMissed?: (event: AppHomeStreamEvent) => boolean;
  /**
   * Gate for the mount-time catch-up: gets every key it would invalidate
   * (missed matches, a replayed reconnect, a buffer overflow or throw
   * resync), deduped. Return true to take them yourself, e.g. to defer them
   * while an action is in flight. Default: invalidate at once. Live frames,
   * live reconnects included, never pass through it.
   */
  holdCatchUp?: (keys: readonly QueryKey[]) => boolean;
} & (
  | {
      /** Keys to invalidate for a matching frame (prefix match, like `invalidateQueries`). */
      keys: readonly QueryKey[];
      /** Default `'invalidate'`: resync `keys`. */
      onReconnect?: StreamReconnectPolicy;
    }
  | {
      /** Keys per frame. On a reconnect the function gets the `open` frame. */
      keys: (event: AppHomeStreamEvent) => readonly QueryKey[];
      /**
       * Required: a keys function returns nothing useful for an `open` frame
       * unless it handles one, so a default would silently resync nothing.
       */
      onReconnect: StreamReconnectPolicy;
    }
);

/**
 * Invalidate React Query caches from the app-home SSE stream. The sanctioned
 * path for server-pushed cache updates: it sees every frame, in order, and
 * resyncs after a reconnect.
 *
 * A reconnect is an `open` after a real drop (see `subscribeAppHomeEvents`); a
 * tab refocus that swaps a healthy source does not resync. On unmount, keys
 * still waiting for the window's end are invalidated, not dropped.
 *
 * Frames sent while the hook was not subscribed are caught up on mount: the
 * hook reads the store's buffered frames after its cursor (taken at its last
 * unmount, or at first render) and runs them through `whenMissed` (default
 * `when`) and `keys` as one leading-edge invalidation. That covers a route
 * left and re-entered while the shell keeps the stream open, and a frame that
 * lands between commit and the passive effect. If the buffer no longer holds
 * them all, the hook resyncs through `onReconnect`. The unmount cursor is kept
 * per QueryClient under the hash of the hook's reconnect keys (`keys` for an
 * `open` frame), so two hooks with the same reconnect keys share it.
 *
 * Known gap: if every subscriber of the stream unmounts at once (a whole-tree
 * remount), the EventSource closes, and frames the server sends before the next
 * mount are never received, so neither a reconnect nor the catch-up sees them.
 *
 * Shares the `active`/`all` stream with every other app-home consumer, so it
 * opens no second EventSource.
 */
export function useStreamInvalidation(options: StreamInvalidationOptions): void {
  const queryClient = useQueryClient();
  // The stream cursor at first render. Frames after it and before the effect
  // subscribes (React runs passive effects after paint) are read back on mount.
  const [renderSeq] = useState(() => getAppHomeStreamSeq(STREAM_KEY));
  const optionsRef = useRef(options);
  useLayoutEffect(() => {
    optionsRef.current = options;
  });

  useEffect(() => {
    // Lifetime state is local to this mount, so a StrictMode replay or a real
    // remount starts clean.
    const pending = new Map<string, QueryKey>();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const invalidate = (keys: Iterable<QueryKey>) => {
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    };
    const openWindow = () => {
      timer = setTimeout(closeWindow, optionsRef.current.coalesceMs ?? DEFAULT_COALESCE_MS);
    };
    // Trailing flush. The window re-arms only when it flushed something, so a
    // quiet window closes and the next match is a fresh leading edge.
    const closeWindow = () => {
      timer = null;
      if (pending.size === 0) return;
      const keys = [...pending.values()];
      pending.clear();
      invalidate(keys);
      openWindow();
    };

    const enqueue = (keys: readonly QueryKey[]) => {
      if (keys.length === 0) return;
      const unique = new Map(keys.map((key) => [hashKey(key), key] as const));
      if (timer) {
        for (const [hash, key] of unique) pending.set(hash, key);
        return;
      }
      invalidate(unique.values());
      openWindow();
    };

    const resolveKeys = (event: AppHomeStreamEvent): readonly QueryKey[] => {
      const { keys } = optionsRef.current;
      return typeof keys === 'function' ? keys(event) : keys;
    };

    const shouldResync = (): boolean => {
      const { onReconnect = 'invalidate' } = optionsRef.current;
      return typeof onReconnect === 'function' ? onReconnect(queryClient) : onReconnect === 'invalidate';
    };
    const matchKeys = (
      event: AppHomeStreamEvent,
      { reconnect }: AppHomeEventMeta,
      when: (event: AppHomeStreamEvent) => boolean,
    ): readonly QueryKey[] => (reconnect && shouldResync()) || when(event) ? resolveKeys(event) : [];
    // Runs the caller's `keys`, so a throw means no cursor: the mount falls
    // back to its render cursor and the unmount records nothing.
    const cursorId = (): string | null => {
      try {
        return hashKey(resolveKeys(OPEN_FRAME));
      } catch (err) {
        logger.warn({ err }, '[sse] stream cursor id threw');
        return null;
      }
    };

    const cursors = cursorsFor(queryClient);
    const mountId = cursorId();
    const since = (mountId === null ? undefined : cursors.get(mountId)) ?? renderSeq;
    if (mountId !== null) cursors.delete(mountId);

    const unsubscribe = subscribeAppHomeEvents(STREAM_KEY, (event, meta) => {
      // One enqueue per frame, so a frame that is both a reconnect and a match
      // fires once at the leading edge rather than once now and once at the end.
      enqueue(matchKeys(event, meta, optionsRef.current.when));
    });

    // Catch up on frames sent while unmounted or before this effect ran, as one
    // leading-edge enqueue. A cursor older than the buffer, or a predicate or
    // keys function that throws on a missed frame, resyncs like a reconnect.
    const catchUp = (keys: readonly QueryKey[]) => {
      if (keys.length === 0) return;
      const unique = [...new Map(keys.map((key) => [hashKey(key), key] as const)).values()];
      let held = false;
      try {
        held = optionsRef.current.holdCatchUp?.(unique) === true;
      } catch (err) {
        logger.warn({ err }, '[sse] stream holdCatchUp threw');
      }
      if (!held) enqueue(unique);
    };
    const resyncAsReconnect = () => {
      try {
        if (shouldResync()) catchUp(resolveKeys(OPEN_FRAME));
      } catch (err) {
        logger.warn({ err }, '[sse] stream catch-up resync threw');
      }
    };
    const missed = readAppHomeEventsSince(STREAM_KEY, since);
    if (missed === null) {
      resyncAsReconnect();
    } else {
      let keys: QueryKey[] | null;
      try {
        const { when, whenMissed = when } = optionsRef.current;
        keys = missed.flatMap(({ event, meta }) => matchKeys(event, meta, whenMissed));
      } catch (err) {
        logger.warn({ err }, '[sse] stream catch-up threw');
        keys = null;
      }
      if (keys === null) resyncAsReconnect();
      else catchUp(keys);
    }

    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
      timer = null;
      // Flush, do not drop: the QueryClient outlives this component, and a key
      // that matched inside the window would otherwise stay stale (most of these
      // queries use staleTime: Infinity). `pending` is per mount and only ever
      // holds keys that matched, so a StrictMode replay flushes nothing.
      const keys = [...pending.values()];
      pending.clear();
      invalidate(keys);
      // Last, so a throwing `keys` cannot skip the flush above.
      const unmountId = cursorId();
      if (unmountId !== null) cursors.set(unmountId, getAppHomeStreamSeq(STREAM_KEY));
    };
  }, [queryClient, renderSeq]);
}
